import { Work, WorkEvent, WorkEventType } from "../../types/works";

/**
 * Auto-calculation helpers for the driver execution view.
 *
 * The backend is the source of truth and recomputes these on each round/complete;
 * these client mirrors drive the LIVE preview between server round-trips (tonnage
 * /distance from completed rounds, hours from start→now). Once a metric is edited
 * by hand (`autoCalcDisabled`), the auto value stops overwriting it. Vehicle
 * capacity comes from the denormalized `work.vehicleCapacityTons`.
 */

/** Tonnage auto-derived from completed rounds × vehicle capacity (cyclic). */
export const autoTonnage = (work: Work): number | undefined => {
  if (work.tonnage.autoCalcDisabled) return work.tonnage.actual;
  const capacity = work.vehicleCapacityTons;
  if (work.type === "cyclic" && capacity && work.roundsActual) {
    return Math.round(work.roundsActual * capacity * 10) / 10;
  }
  return work.tonnage.actual;
};

/** Distance auto-derived from completed rounds × planned per-round leg (cyclic). */
export const autoDistance = (work: Work): number | undefined => {
  if (work.distanceKm.autoCalcDisabled) return work.distanceKm.actual;
  if (
    work.type === "cyclic" &&
    work.roundsPlanned &&
    work.distanceKm.planned &&
    work.roundsActual
  ) {
    const perRound = work.distanceKm.planned / work.roundsPlanned;
    return Math.round(work.roundsActual * perRound);
  }
  return work.distanceKm.actual;
};

/** Decimal hours elapsed between actualStart and `end` (default now), minus paused spans. */
export const elapsedHours = (work: Work, end: Date = new Date()): number | undefined => {
  if (work.hours.autoCalcDisabled) return work.hours.actual;
  if (!work.actualStart) return work.hours.actual;
  const start = new Date(work.actualStart).getTime();
  let pausedMs = 0;
  let pauseStart: number | null = null;
  for (const ev of work.events) {
    if (ev.type === "pause") pauseStart = new Date(ev.timestamp).getTime();
    else if (ev.type === "resume" && pauseStart !== null) {
      pausedMs += new Date(ev.timestamp).getTime() - pauseStart;
      pauseStart = null;
    }
  }
  if (pauseStart !== null) pausedMs += end.getTime() - pauseStart;
  const ms = Math.max(0, end.getTime() - start - pausedMs);
  return Math.round((ms / 3_600_000) * 100) / 100;
};

/** Whether the work is currently paused (last pause/resume event is a pause). */
export const isPaused = (work: Work): boolean => {
  for (let i = work.events.length - 1; i >= 0; i--) {
    const type = work.events[i].type;
    if (type === "pause") return true;
    if (type === "resume" || type === "start") return false;
  }
  return false;
};

/** Ordered pickup→delivery steps and the next one the driver should record. */
export const PICKUP_DELIVERY_STEPS: WorkEventType[] = [
  "arrived_pickup",
  "loaded",
  "arrived_delivery",
  "unloaded",
];

export const nextPickupDeliveryStep = (work: Work): WorkEventType | null => {
  const done = new Set(work.events.map((e) => e.type));
  return PICKUP_DELIVERY_STEPS.find((s) => !done.has(s)) ?? null;
};

/** Short "HH:MM" for event log rows. */
export const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

/** "59.43, 24.74" or undefined when geo was unavailable. */
export const formatCoords = (coords?: WorkEvent["coordinates"]): string | undefined =>
  coords ? `${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}` : undefined;
